import React, { useRef } from "react";
import { Box } from "@material-ui/core";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import { parseLocalDate } from "../../../utils";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const COLORS = [
  "#0072B2",
  "#D55E00",
  "#009E73",
  "#CC79A7",
  "#F0E442",
  "#56B4E9",
];

const CorrelationScatterChart = ({ series, xLabel, yLabel, height = 450 }) => {
  const chartRef = useRef();

  const datasets = series.flatMap((s, i) => {
    const color = COLORS[i % COLORS.length];
    const xs = s.points.map((p) => p.x);
    const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];
    const trendLine = [
      { x: minX, y: s.slope * minX + s.intercept },
      { x: maxX, y: s.slope * maxX + s.intercept },
    ];

    return [
      {
        label: `${s.name} 95% CI lower`,
        data: s.lowerBand,
        type: "line",
        borderWidth: 0,
        pointRadius: 0,
        backgroundColor: color + "33",
        fill: false,
        order: 1,
      },
      {
        label: `${s.name} 95% CI upper`,
        data: s.upperBand,
        type: "line",
        borderWidth: 0,
        pointRadius: 0,
        backgroundColor: color + "33",
        fill: "-1",
        order: 1,
      },
      {
        label: `${s.name} Samples`,
        data: s.points.filter(
          (p) => !s.outliers.some((o) => o.date === p.date)
        ),
        backgroundColor: color,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: `${s.name} Outliers`,
        data: s.outliers,
        backgroundColor: "rgba(255,255,255,0.8)",
        borderColor: color,
        borderWidth: 1,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointStyle: "circle",
      },
      {
        label: `${s.name} Trend`,
        data: trendLine,
        type: "line",
        borderColor: `${color}B3`,
        borderWidth: 3,
        borderDash: [6, 4],
        pointRadius: 0,
        fill: false,
      },
    ];
  });

  if (!series || !series.length) return null;

  return (
    <Box height={height} width="100%">
      <Scatter
        ref={chartRef}
        data={{ datasets }}
        options={{
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              padding: 12,
              borderWidth: 1,
              borderColor: "#999",
              backgroundColor: "#fff",
              titleColor: "#333",
              bodyColor: "#333",
              cornerRadius: 6,
              callbacks: {
                label: (ctx) => {
                  const { dataset, raw, label, formattedValue } = ctx;
                  const location = dataset.label.replace(
                    / (Outliers|Samples)$/,
                    ""
                  );
                  const isOutlier = dataset.label.includes("Outlier");
                  const date = raw?.date
                    ? parseLocalDate(raw.date).toLocaleDateString()
                    : undefined;
                  return [
                    date,
                    `${location}: (${label} ${xLabel}, ${formattedValue} ${yLabel})`,
                    isOutlier ? "⚠️ Outlier" : null,
                  ].filter(Boolean);
                },
              },
            },
            legend: {
              labels: {
                filter: (item) => !item.text.includes("95% CI"),
              },
              onClick: (e, legendItem, legend) => {
                const chart = legend.chart;
                const prefix = legendItem.text.replace(" Trend", "");
                chart.data.datasets.forEach((d, i) => {
                  if (
                    d.label.startsWith(prefix) &&
                    d.label.includes("95% CI")
                  ) {
                    chart.getDatasetMeta(i).hidden =
                      !chart.getDatasetMeta(i).hidden;
                  }
                });
                const meta = chart.getDatasetMeta(legendItem.datasetIndex);
                meta.hidden = !meta.hidden;
                chart.update();
              },
            },
          },
          scales: {
            x: {
              title: { display: true, text: xLabel },
              grid: { color: "rgba(0,0,0,0.1)" },
            },
            y: {
              title: { display: true, text: yLabel },
              grid: { color: "rgba(0,0,0,0.1)" },
            },
          },
        }}
      />
    </Box>
  );
};

export default CorrelationScatterChart;
